import React, { FC, useRef } from "react"
import "#/lib/trpc/client"
import { Redirect, Route, useLocation } from "react-router-dom"
import { Title } from "#/services/page-title/Title"
import Transactions from "#/components/views/reimbursements/Transactions"
import ResetPasswordContainer from "../../containers/login/ResetPasswordContainer"
import ResetPasswordSuccess from "../../../pages/reset-password-success"
import ResetPasswordError from "#/pages/reset-password-error"
import { TalkToCarrotRouter } from "../../talk-to-carrot/TalkToCarrotRouter"
import MerchantDeclineContainer from "../../containers/merchantdecline/MerchantDeclineContainer"
import { ReimbursementsRouter } from "#/components/views/reimbursements/ReimbursementsRouter"
import { BenefitNotStartedContainer } from "#/components/views/modal/BenefitNotStarted"
import { PageNotFound } from "#/components/views/error/PageNotFound"
import { LibraryRouter } from "#/components/containers/library/LibraryRouter"
import { CarrotCollectiveRoute } from "#/components/carrot-collective/CarrotCollectiveRoute"
import ActivateContainer from "../../containers/activate/ActivateContainer"
import { YourBenefitGuideRoute } from "./routes/YourBenefitGuideRoute"
import { DynamicContentTool } from "#/components/dynamic-content/DynamicContentTool"
import {
  DBGRevamp2022Q4Redirects,
  Paths,
  ReadAndLearnGestationalSurrogacyRenameRedirects,
  ReadAndLearnPregnancyRenameRedirects,
  DBGJourneyModularityPhase3Redirects,
  SupportToCoverageRedirects,
  ChestfeedingAndHumanMilkRedirects,
  MenopauseRedirects,
  DBGChildToParentRedirects,
  CarrotCollectiveRedirects,
  DBGToTelemedicineForMenopauseCareRedirects,
  DBGToSprintsRedirects
} from "#/utils/Paths"
import { CarrotPlansRouter } from "#/components/carrot-plans/CarrotPlansRouter"
import LockedDownRoute from "./LockedDownRoute"
import { LockedDownGetHelpPage } from "../../talk-to-carrot/TalkToCarrotLockedDownGetHelp"
import HealthInsuranceDetailsPage from "#/pages/health-insurance-details"
import CarrotPlanRequiredRedirect from "../../../pages/carrot-plan-required-redirect"
import { getBenefitStarted } from "#/redux/derivedSelectors"
import { useSelector } from "react-redux"
import { TalkToCarrotHelpCenter } from "../../talk-to-carrot/TalkToCarrotHelpCenter"
import { ProviderFinderAdapter } from "./domainIsolation/ProviderFinderAdapter"
import { SendMessagePage } from "#/pages/talk-to-carrot/send-a-message"
import VerifyPersonalEmail from "../../EmailVerification/VerifyPersonalEmail"
import TokenExpired from "../../EmailVerification/TokenExpired"
import WrongPersonalEmail from "../../EmailVerification/WrongPersonalEmail"
import NoPersonalEmail from "../../EmailVerification/NoPersonalEmail"
import { ImmersiveGuide } from "#/pages/guides/ImmersiveGuide"
import { MessagingEmailVerificationModal } from "../../EmailVerification/MessagingEmailVerificationModal"
import Settings from "../../../utils/CarrotConfig"
import BankDetailsPage from "#/pages/bank-details/BankDetailsPage"
import MemberIdPage from "#/pages/member-id/MemberIdPage"
import ConsumerSubscriptionBillingPage from "#/pages/consumer-subscription-billing/ConsumerSubscriptionBillingPage"
import LegacyBillingPathRedirect from "#/pages/consumer-subscription-billing/LegacyBillingPathRedirect"
import ConsumerFaqPage from "#/pages/consumer-faq/ConsumerFaqPage"
import PaywallPage from "#/pages/paywall/PaywallPage"
import UpdateRoutingFlow from "../../carrot-plans/shared/UpdateRoutingFlow"
import MedicalQuestionsMenuItemRoutingFlow from "#/components/carrot-plans/steps/medical-questions/MedicalQuestionsMenuItemRoutingFlow"
import { GatedExitRoutingFlowWarning } from "#/components/carrot-plans/case-rate-renewals/GatedExitRoutingFlowWarning"
import { CarrotToaster } from "#/components/toasts/CarrotToaster"
import { AccountDeletionPage } from "#/components/account-deletion"
import { isMobileApp, setMobileNativeUserInfo } from "#/lib/carrot-api/MobileNativeClient"
import { InboundMemberPaymentsAdapter } from "#/pages/inbound-member-payments/InboundMemberPaymentsAdapter"
import { SettingsContainer } from "#/pages/account"
import { AppLoginModalContainer } from "../modal/AppLoginModalContainer"
import { NavLayout } from "./navigation/NavLayout"
import { PhoneSupportRouter } from "./PhoneSupportRouter"
import { Switch } from "#/services/routing/Switch"
import { useConsumerRoutesEnabled } from "#/services/launchdarkly/useConsumerRoutesEnabled"
import { CarrotPartnerBooking } from "#/pages/provider-finder/components/results/BookingExperience/CarrotPartnerBooking"
import { useCurrentUser } from "#/components/context/user/UserContext"
import { MobileAppModalContainer } from "../modal/MobileAppModalContainer"
import {
  ArtMedicalRecordsConsentPage,
  PregnancyMedicalRecordsConsentPage,
  FertilityPreservationMedicalRecordsConsentPage
} from "#/components/medical-records-consent/MedicalRecordsConsentPage"
import useHeapTrackingReactQuery from "../../../utils/hooks/useHeapTrackingReactQuery"
import { TelemedicineForMenopauseLandingPage } from "#/components/telemedicine/TelemedicineForMenopauseLandingPage"
import { TelemedicineUnauthorizedPage } from "#/components/telemedicine/TelemedicineUnauthorizedPage"
import { CarrotMatchPageWrapper } from "#/components/carrot-match-fertility-clinic-precision-matching/CarrotMatchLandingPage"
import { SprintsLandingPageRoute } from "#/pages/sprints/SprintsLandingPageRoute"
import CheckInExperienceRoutingFlow from "#/components/carrot-plans/check-in/CheckInExperienceRoutingFlow"
import { RtxConsent } from "#/components/consent/RtxConsent"
import { FreedomFertilityConsent } from "#/components/consent/FreedomFertilityConsent"
import { Faqs } from "#/pages/faqs/page"
import { FaqsCategoryPage } from "#/pages/faqs/categories/[categorySlug]/page"
import { FaqsArticle } from "#/pages/faqs/[vanityAndId]/page"
import { ContentfulControlledPageRoutes } from "#/features/contentful/contentful-controlled-pages"
import { CommunicationPreferencesRedirect } from "#/components/views/account/CommunicationPreferencesRedirect"
import { TelemedicineBookingPage } from "#/pages/telemedicine/booking/page"
import { TelemedicinePreVisitIntakePage } from "#/pages/telemedicine/booking/forms/page"
import { TelemedicineResolverPage } from "#/pages/telemedicine/resolver/page"
import { isProductionEnv } from "#/utils/env"
import { CarrotPlanRoute } from "./routes/CarrotPlanRoute"
import { DevToolsRoute } from "./routes/DevToolsRoute"
import { MessagesRoute } from "./routes/MessagesRoute"
import { CarrotRxRoute } from "./routes/CarrotRxRoute"
import { AppointmentsRoute } from "./routes/AppointmentsRoute"
import { EnterpriseOnlyRoute } from "./routes/EnterpriseOnlyRoute"
import { BookingTrackingProvider } from "#/services/memberActionTracking/BookingTrackingProvider"
import { PAYWALL_PATH, PaywallGate } from "./routes/PaywallGate"

function useAnchorLinkScrollFix(): void {
  const { pathname, hash, key } = useLocation()
  const priorPathname = useRef(null)
  React.useEffect(() => {
    // Scrolling to anchor links is apparently not supported in react-router
    // Here we can scroll to ID'd elements that match our hash (Ex. #bankdetails will scroll to the element with id: bankdetails)
    // Fix lifted from: https://github.com/remix-run/react-router/issues/394#issuecomment-220221604
    if (hash !== "") {
      window.requestAnimationFrame(() => {
        setTimeout(() => {
          const id = hash.replace("#", "")
          const element = document.getElementById(id)
          if (element) {
            element.scrollIntoView()
          }
        }, 0)
      })
    } else if (priorPathname.current !== pathname) {
      //Scrolls the page to the top only if the pathname changes
      window.scrollTo(0, 0)
    }
    priorPathname.current = pathname
  }, [pathname, hash, key, priorPathname])
}

function useRedirectOnBenefitNotStarted(): boolean {
  const benefitStarted = useSelector(getBenefitStarted)
  const location = useLocation()
  const onHomePage = React.useMemo(() => location.pathname === "/", [location.pathname])
  const shouldRedirect = React.useMemo(() => !onHomePage && !benefitStarted, [benefitStarted, onHomePage])
  return shouldRedirect
}

function useSetMobileNativeUserInfo() {
  const { email, employeeId } = useCurrentUser()
  if (isMobileApp()) {
    setMobileNativeUserInfo(email, employeeId)
  }
}

// Paid content mounted OUTSIDE NavLayout has to wall itself. The gate lives inside the
// navigation chrome so it can hold or redirect without tearing the chrome down with it, which
// puts these chromeless routes out of its reach — they have no chrome to keep, so wrapping them
// here costs nothing. Any future chromeless route carrying member content needs the same wrap.
const PaywalledImmersiveGuide: FC = () => (
  <PaywallGate>
    <ImmersiveGuide />
  </PaywallGate>
)

const App: FC = () => {
  const shouldRedirect = useRedirectOnBenefitNotStarted()
  useHeapTrackingReactQuery()
  useAnchorLinkScrollFix()
  useSetMobileNativeUserInfo()
  const location = useLocation<{ showVerifyEmailModal: boolean }>()
  // Same hook Root.tsx gates its `/consumer` routes on — read it here rather than
  // `useFlags` so the two cannot disagree. If this said `consumerAppSignUp` from
  // `useFlags` while Root failed open, a signed-in member would reach AppContainer and
  // then fall through to PageNotFound below.
  const consumerAppSignUp = useConsumerRoutesEnabled()

  return (
    <Switch allowedRouters={[LockedDownRoute, EnterpriseOnlyRoute]}>
      {/* Routes here are for authenticated pages that do not have side navigation */}
      {shouldRedirect && <Redirect to="/" />}
      {/* We ran into an issue where the type definition doesn't match the version of react-router-dom we're using. */}
      {/* @ts-expect-error TS(2769) FIXME: No overload matches this call. */}
      <Route from="/reset-password" component={ResetPasswordContainer} />
      {/* @ts-expect-error TS(2769) FIXME: No overload matches this call. */}
      <Route from="/reset-password-success" component={ResetPasswordSuccess} />
      <Route path="/reset-password-error" component={ResetPasswordError} />
      <EnterpriseOnlyRoute exact path="/carrot-plans/case-rate-renewal/routing" component={UpdateRoutingFlow} />
      <EnterpriseOnlyRoute exact path="/carrot-plans/journey-change/routing" component={UpdateRoutingFlow} />
      <EnterpriseOnlyRoute exact path="/carrot-plans/check-in/routing" component={CheckInExperienceRoutingFlow} />
      <EnterpriseOnlyRoute exact path="/medical-questions/:journey" component={MedicalQuestionsMenuItemRoutingFlow} />
      <EnterpriseOnlyRoute exact path="/refresh-carrot-plan-leave-warning" component={GatedExitRoutingFlowWarning} />
      <EnterpriseOnlyRoute path="/carrot-plans" component={CarrotPlansRouter} />
      <Route exact path="/communication-preferences-redirect" component={CommunicationPreferencesRedirect} />
      <Route exact path="/token-expired" component={TokenExpired} />
      <Route exact path="/wrong-personal-email" component={WrongPersonalEmail} />
      <Route exact path="/no-personal-email" component={NoPersonalEmail} />
      <Route path="/confirm/:token" component={MerchantDeclineContainer} />
      <LockedDownRoute path={["/read/:category/guide/:guide/:section"]} component={PaywalledImmersiveGuide} />
      <Route exact path={"/action-tracking/consent"} component={RtxConsent} />
      <Route exact path={"/data-sharing/consent"} component={FreedomFertilityConsent} />
      <LockedDownRoute exact path="/art-records-consent" component={ArtMedicalRecordsConsentPage} />
      <LockedDownRoute exact path="/pregnancy-records-consent" component={PregnancyMedicalRecordsConsentPage} />
      <LockedDownRoute
        exact
        path="/preservation-records-consent"
        component={FertilityPreservationMedicalRecordsConsentPage}
      />

      {/* Entry pages and booking share this layout, so correlation survives navigation between them. */}
      <Route
        render={() => (
          <BookingTrackingProvider>
            <NavLayout>
              <Title />
              <DynamicContentTool />
              <BenefitNotStartedContainer />
              <AppLoginModalContainer />
              {location.state?.showVerifyEmailModal && <MessagingEmailVerificationModal />}
              <CarrotToaster />
              {isMobileApp() && <MobileAppModalContainer />}
              {/* Inside NavLayout, wrapping only the route table: the paywall is an allow-list,
                  so anything not named in PAYWALL_ALLOW_LIST is walled by default and never
                  paints before the redirect — while the navigation, which the AC requires a
                  walled member to keep, stays mounted through both the wait and the redirect. */}
              <PaywallGate>
                <Switch allowedRouters={[LockedDownRoute, PhoneSupportRouter, EnterpriseOnlyRoute]}>
                  <Route exact path="/" component={CarrotPlanRoute} />
                  <Redirect
                    exact
                    from="/doula-attestation"
                    to="/your-benefit-guide/coverage/pregnancy-and-postpartum"
                  />
                  <LockedDownRoute exact path="/clinic-match" component={CarrotMatchPageWrapper} />

                  {CarrotCollectiveRedirects.map(({ from, to }) => (
                    <Redirect key={from} from={from} to={to} />
                  ))}

                  {DBGRevamp2022Q4Redirects.map(({ from, to }) => (
                    <Redirect key={from} {...{ from, to }} />
                  ))}
                  {ReadAndLearnPregnancyRenameRedirects.map(({ from, to }) => (
                    <Redirect key={from} {...{ from, to }} />
                  ))}
                  {ReadAndLearnGestationalSurrogacyRenameRedirects.map(({ from, to }) => (
                    <Redirect key={from} {...{ from, to }} />
                  ))}
                  {DBGJourneyModularityPhase3Redirects.map(({ from, to }) => (
                    <Redirect key={from} {...{ from, to }} />
                  ))}
                  {SupportToCoverageRedirects.map(({ from, to }) => (
                    <Redirect key={from} {...{ from, to }} />
                  ))}
                  <Route exact path={Paths.SPRINTS_LANDING_PAGE} component={SprintsLandingPageRoute} />
                  {ChestfeedingAndHumanMilkRedirects.map(({ from, to }) => (
                    <Redirect key={from} {...{ from, to }} />
                  ))}
                  {MenopauseRedirects.map(({ from, to }) => (
                    <Redirect key={from} {...{ from, to }} />
                  ))}
                  {DBGChildToParentRedirects.map(({ from, to }) => (
                    <Redirect key={from} {...{ from, to }} />
                  ))}
                  {DBGToTelemedicineForMenopauseCareRedirects.map(({ from, to }) => (
                    <Redirect key={from} {...{ from, to }} />
                  ))}
                  {DBGToSprintsRedirects.map(({ from, to }) => (
                    <Redirect key={from} {...{ from, to }} />
                  ))}
                  {/* Routes here are for authenticated pages with side navigation */}
                  <Redirect from="/insurance-details" to="/health-insurance-details" />
                  <EnterpriseOnlyRoute exact path="/health-insurance-details" component={HealthInsuranceDetailsPage} />
                  <Route path="/help-center/contact-care-team" component={TalkToCarrotHelpCenter} />
                  <Redirect from="/help-center/send-message" to="/send-a-message" />
                  <Route exact path="/faqs" component={Faqs} />
                  <Route exact path="/faqs/categories/:categorySlug" component={FaqsCategoryPage} />
                  <Route exact path="/faqs/:vanityAndId" component={FaqsArticle} />
                  <Route path="/send-a-message" component={SendMessagePage} />
                  <Route path="/get-help" component={LockedDownGetHelpPage} />
                  <LockedDownRoute path="/ava-bracelet" component={TalkToCarrotRouter} />
                  <Redirect from="/journeywelcome" to="/" />
                  <Redirect from="/journey" to="/" />
                  <Redirect from="/journey/article/:id" to="/" />
                  <LockedDownRoute path="/transactions" component={Transactions} />
                  <LockedDownRoute path="/reimbursements" component={ReimbursementsRouter} />
                  <Route exact path="/account/delete" component={AccountDeletionPage} />
                  <Route exact path="/account" component={SettingsContainer} />
                  <Route exact path="/bank-details" component={BankDetailsPage} />
                  <Route exact path="/member-id" component={MemberIdPage} />
                  {/* Dev tools. Registered only outside production; in prod the path falls
                    through to PageNotFound. The route component owns the data hook so it
                    runs only on a match — see DevToolsRoute. */}
                  {!isProductionEnv() && <Route path={Paths.DEV_TOOLS} component={DevToolsRoute} />}
                  {consumerAppSignUp && (
                    <Route exact path="/consumer/billing-detail" component={ConsumerSubscriptionBillingPage} />
                  )}
                  {/* Pre-rename path — see LegacyBillingPathRedirect for why the redirect is a
                    component and not a bare `<Redirect from to>`. Separate `{cond && <Route/>}`
                    child, gated with the route above so the legacy path 404s outright (rather than
                    redirecting into a 404) when the flag is off. */}
                  {consumerAppSignUp && (
                    <Route exact path="/consumer/subscription-billing" component={LegacyBillingPathRedirect} />
                  )}
                  {consumerAppSignUp && <Route exact path="/consumer/faq" component={ConsumerFaqPage} />}
                  {/* Rides the same flag as the route gate that redirects here, so neither can exist
                    without the other — a gate pointing at an unmounted path would 404 the member
                    it walls. */}
                  {consumerAppSignUp && <Route exact path={PAYWALL_PATH} component={PaywallPage} />}
                  <Route path="/make-a-payment" component={InboundMemberPaymentsAdapter} />
                  {/* NON-EXACT: everything under /messages is one mount, and the grammar
                    (`MessagesShell`) lives with the page rather than here. */}
                  <Route path="/messages" component={MessagesRoute} />
                  <Redirect from="/library" to="/read" />
                  <Route path="/read" component={LibraryRouter} />
                  <Route exact path="/exclusive-discounts" component={CarrotCollectiveRoute} />
                  <Redirect from="/about" to="/your-benefit-guide/about-your-benefit" />
                  <Redirect from="/eligible-expenses" to="/your-benefit-guide/coverage" />
                  <Route exact path="/carrot-rx" component={CarrotRxRoute} />
                  <Route
                    path="/providerfinder"
                    render={({ location }) => (
                      <Redirect
                        to={{
                          ...location,
                          pathname: location.pathname.replace(/^\/providerfinder/, Paths.PROVIDER_FINDER)
                        }}
                      />
                    )}
                  />
                  <LockedDownRoute
                    exact
                    path={`${Paths.PROVIDER_FINDER}/requestappointment/:providerName/:providerUuid?`}
                    component={CarrotPartnerBooking}
                  />
                  <LockedDownRoute
                    path={`${Paths.PROVIDER_FINDER}/telemedicine-for-menopause`}
                    component={TelemedicineForMenopauseLandingPage}
                  />
                  <Redirect
                    from={`${Paths.PROVIDER_FINDER}/telemedicine-unauthorized`}
                    to="/telemedicine-unauthorized"
                  />
                  <Route exact path="/telemedicine-unauthorized" component={TelemedicineUnauthorizedPage} />
                  <Route exact path={Paths.TELEMEDICINE} component={TelemedicineResolverPage} />
                  <Route exact path={Paths.TELEMEDICINE_BOOKING} component={TelemedicineBookingPage} />
                  <Route exact path="/telemedicine/booking/:id/forms" component={TelemedicinePreVisitIntakePage} />
                  <Route exact path={Paths.TELEMEDICINE_APPOINTMENTS} component={AppointmentsRoute} />

                  {ContentfulControlledPageRoutes.map(({ path, component }) => (
                    <Route key={path} exact path={path} component={component} />
                  ))}

                  <LockedDownRoute path={Paths.PROVIDER_FINDER} component={ProviderFinderAdapter} />
                  <Route path={Paths.YOUR_BENEFIT_GUIDE} component={YourBenefitGuideRoute} />
                  <Route exact path="/verify-personal-email/:token" component={VerifyPersonalEmail} />
                  <Redirect exact path="/register/:guid" to={"/"} />
                  <Redirect exact path="/signin" to={"/"} />
                  <Redirect exact path="/signup" to={"/"} />
                  <Redirect exact path="/sign-up" to={"/"} />
                  <Redirect exact path="/login" to={"/"} />
                  <Redirect exact path="/request-reset-password" to={"/"} />
                  <LockedDownRoute path="/activate" component={ActivateContainer} />
                  <EnterpriseOnlyRoute path="/carrot-plan-required" component={CarrotPlanRequiredRedirect} />
                  <PhoneSupportRouter path="/chat" component={TalkToCarrotRouter} />
                  <PhoneSupportRouter path={"/care-team"} component={TalkToCarrotRouter} />

                  <PhoneSupportRouter path={Paths.TALK_TO_CARROT} component={TalkToCarrotRouter} />
                  <Redirect from="/support" to={Paths.FAQS} />
                  <Route
                    path="/awx/kyc"
                    // @ts-expect-error TS(7011) FIXME: Function expression, which lacks return-type annot... Remove this comment to see the full error message
                    component={() => {
                      window.location.replace(Settings.CARROT_BACKEND_URL + "/api/v2/awx/kyc")
                      return null
                    }}
                  />

                  {/* 404 catch all */}
                  <Route component={PageNotFound} />
                </Switch>
              </PaywallGate>
            </NavLayout>
          </BookingTrackingProvider>
        )}
      />
    </Switch>
  )
}

export { App }
